import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import { useIntl } from 'react-intl';
import Height from './Height';
import Depth from './Depth';
import { useCore } from 'contexts/core';
import { changeSectionSize } from 'rangeConfig/bror/utils/changeSize/sectionUtils';
import { Dimension } from '@inter-ikea-storageone/range';
import { getFilterOptions } from 'filters/SizeFilter/utils';
import { getMatchingSections } from '../../utils/changeSize/productUtils';
import { useBehaviorTracker } from 'contexts/behaviors';

const SizeContainer = styled.div`
  padding: ${({ theme }) => theme.space.s} 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xxl};
`;

const ChangeSize = () => {
  const { formatMessage } = useIntl();
  const products = useSelector(getMarketProducts);
  const selectedProduct = useSelector(getSelectedProduct);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  const sectionsWithSameHeight = getMatchingSections(selectedProduct, products, 'height');
  const sectionsWithSameDepth = getMatchingSections(selectedProduct, products, 'depth');
  const availableDepths = getFilterOptions('depth')(sectionsWithSameHeight);
  const availableHeights = getFilterOptions('height')(sectionsWithSameDepth);

  const selectedEntity = coreApi.entities.findSelectedEntity();

  // Changes the height of the selected section
  const handleSizeChange = (
    size: number,
    setFilterState: any,
    sectionsMatchingSelectedSection: any,
    dimensionType: Dimension
  ) => {
    const meta = { size: { dimension: dimensionType, value: size } };

    behaviorTracker.withMeta(meta).custom(() => {
      setFilterState({ selected: size });

      if (!selectedEntity) return;

      const newSectionToSelect = changeSectionSize(
        coreApi,
        sectionsMatchingSelectedSection,
        size,
        selectedEntity,
        dimensionType
      );

      if (newSectionToSelect) {
        coreApi.entities.select(newSectionToSelect);
      }
    });
  };

  return (
    <SizeContainer>
      <Grid>
        <Height
          fluidWidth={true}
          title={formatMessage({ id: 'storageone.height' })}
          products={products}
          selectedProduct={selectedProduct}
          onSizeChange={handleSizeChange}
          sectionsWithSameDepth={sectionsWithSameDepth}
          availableHeights={availableHeights}
        />
        <Depth
          fluidWidth={true}
          title={formatMessage({ id: 'storageone.depth' })}
          products={products}
          selectedProduct={selectedProduct}
          onSizeChange={handleSizeChange}
          sectionsWithSameHeight={sectionsWithSameHeight}
          availableDepths={availableDepths}
        />
      </Grid>
    </SizeContainer>
  );
};

export default ChangeSize;
